import { io, Socket } from 'socket.io-client';
import api from './api';

let socket: Socket | null = null;

export function getSocket(): Socket {
  if (socket) return socket;
  const token = localStorage.getItem('auth_token') || '';
  const baseURL = (api as any).defaults?.baseURL || 'http://localhost:5000/api';
  const origin = baseURL.replace(/\/?api\/?$/, '');
  socket = io(origin, { auth: { token }, withCredentials: true });
  return socket;
}

export function onScreenshareRequest(handler: (fromUserId: string) => void) {
  const s = getSocket();
  s.on('screenshare:request', ({ fromUserId }) => handler(fromUserId));
}

export function sendScreenshareRequest(targetUserId: string) {
  const s = getSocket();
  s.emit('screenshare:request', { targetUserId });
}

export function onScreenshareConsent(handler: (fromUserId: string, accepted: boolean) => void) {
  const s = getSocket();
  s.on('screenshare:consent', ({ fromUserId, accepted }) => handler(fromUserId, accepted));
}

export function sendScreenshareConsent(toUserId: string, accepted: boolean) {
  const s = getSocket();
  s.emit('screenshare:consent', { toUserId, accepted });
}

export function onOffer(handler: (fromUserId: string, sdp: any) => void) {
  getSocket().on('screenshare:offer', ({ fromUserId, sdp }) => handler(fromUserId, sdp));
}
export function onAnswer(handler: (fromUserId: string, sdp: any) => void) {
  getSocket().on('screenshare:answer', ({ fromUserId, sdp }) => handler(fromUserId, sdp));
}
export function onIce(handler: (fromUserId: string, candidate: any) => void) {
  getSocket().on('screenshare:ice', ({ fromUserId, candidate }) => handler(fromUserId, candidate));
}
export function sendOffer(toUserId: string, sdp: any) {
  getSocket().emit('screenshare:offer', { toUserId, sdp });
}
export function sendAnswer(toUserId: string, sdp: any) {
  getSocket().emit('screenshare:answer', { toUserId, sdp });
}
export function sendIce(toUserId: string, candidate: any) {
  getSocket().emit('screenshare:ice', { toUserId, candidate });
}

// Internal chat realtime helpers
export function onInternalChatNewMessage(handler: (payload: { conversationId: string; messageId: string; content: string; type: 'group'|'direct'; roomTitle?: string | null; fromUserId: string; senderDisplayName?: string | null; senderAvatarUrl?: string | null; createdAt?: string | null }) => void) {
  const s = getSocket();
  const fn = (p: any) => handler({
    conversationId: String(p?.conversationId || ''),
    messageId: String(p?.messageId || ''),
    content: String(p?.content || ''),
    type: (p?.type === 'direct' ? 'direct' : 'group') as any,
    roomTitle: p?.roomTitle || null,
    fromUserId: String(p?.fromUserId || ''),
    senderDisplayName: p?.senderDisplayName || null,
    senderAvatarUrl: p?.senderAvatarUrl || null,
    createdAt: p?.createdAt || null
  });
  s.on('internal-chat:new-message', fn);
  return () => { try { s.off('internal-chat:new-message', fn); } catch {} };
}

export function onInternalChatMention(handler: (payload: { conversationId: string; messageId: string; content: string; roomTitle?: string | null; fromUserId: string; createdAt?: string | null }) => void) {
  const s = getSocket();
  const fn = (p: any) => handler({
    conversationId: String(p?.conversationId || ''),
    messageId: String(p?.messageId || ''),
    content: String(p?.content || ''),
    roomTitle: p?.roomTitle || null,
    fromUserId: String(p?.fromUserId || ''),
    createdAt: p?.createdAt || null
  });
  s.on('internal-chat:mention', fn);
  return () => { try { s.off('internal-chat:mention', fn); } catch {} };
}

export function onInternalChatRead(handler: (payload: { conversationId: string; lastReadMessageId: string; fromUserId: string }) => void) {
  const s = getSocket();
  const fn = (p: any) => handler({
    conversationId: String(p?.conversationId || ''),
    lastReadMessageId: String(p?.lastReadMessageId || ''),
    fromUserId: String(p?.fromUserId || '')
  });
  s.on('internal-chat:read', fn);
  return () => { try { s.off('internal-chat:read', fn); } catch {} };
}

export function onInternalChatReaction(handler: (payload: { conversationId: string; messageId: string; fromUserId: string; reaction: 'up'|'down'|'smile'|null }) => void) {
  const s = getSocket();
  const fn = (p: any) => handler({
    conversationId: String(p?.conversationId || ''),
    messageId: String(p?.messageId || ''),
    fromUserId: String(p?.fromUserId || ''),
    reaction: p?.reaction == null ? null : (String(p.reaction) as any)
  });
  s.on('internal-chat:reaction', fn);
  return () => { try { s.off('internal-chat:reaction', fn); } catch {} };
}

export function onInternalChatMessageEdited(handler: (payload: { conversationId: string; messageId: string; content: string }) => void) {
  const s = getSocket();
  const fn = (p: any) => handler({
    conversationId: String(p?.conversationId || ''),
    messageId: String(p?.messageId || ''),
    content: String(p?.content || '')
  });
  s.on('internal-chat:message-edited', fn);
  return () => { try { s.off('internal-chat:message-edited', fn); } catch {} };
}

// Powiadomienia – licznik nieprzeczytanych (push z backendu)
export function onNotificationsUnreadCount(handler: (count: number) => void) {
  const s = getSocket();
  const fn = (p: any) => {
    const count = Number(p?.count ?? p);
    if (!Number.isNaN(count)) handler(count);
  };
  s.on('notifications:unread-count', fn);
  return () => { try { s.off('notifications:unread-count', fn); } catch {} };
}

// STT (transkrypcja) – realtime
export function joinSttMeeting(meetingId: string) {
  const s = getSocket();
  s.emit('stt:join', { meetingId });
}

export function leaveSttMeeting(meetingId: string) {
  const s = getSocket();
  s.emit('stt:leave', { meetingId });
}

export function sendSttPartial(meetingId: string, text: string, localId?: string) {
  const s = getSocket();
  s.emit('stt:partial', { meetingId, text, localId });
}

export function sendSttFinal(meetingId: string, text: string, localId?: string) {
  const s = getSocket();
  s.emit('stt:final', { meetingId, text, localId });
}

export type SttIncoming = {
  meetingId: string;
  userId: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  text: string;
  atIso?: string | null;
  localId?: string | null;
};

export function onSttPartial(handler: (payload: SttIncoming) => void) {
  const s = getSocket();
  const fn = (p: any) => handler({
    meetingId: String(p?.meetingId || ''),
    userId: String(p?.userId || ''),
    displayName: p?.displayName || null,
    avatarUrl: p?.avatarUrl || null,
    text: String(p?.text || ''),
    atIso: p?.atIso || null,
    localId: p?.localId || null
  });
  s.on('stt:partial', fn);
  return () => { try { s.off('stt:partial', fn); } catch {} };
}

export function onSttFinal(handler: (payload: SttIncoming) => void) {
  const s = getSocket();
  const fn = (p: any) => handler({
    meetingId: String(p?.meetingId || ''),
    userId: String(p?.userId || ''),
    displayName: p?.displayName || null,
    avatarUrl: p?.avatarUrl || null,
    text: String(p?.text || ''),
    atIso: p?.atIso || null,
    localId: p?.localId || null
  });
  s.on('stt:final', fn);
  return () => { try { s.off('stt:final', fn); } catch {} };
}

export type SttHistoryEntry = {
  meetingId: string;
  userId: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  text: string;
  atIso?: string | null;
};

// Board presence
export type BoardMember = { userId: string; displayName?: string; avatarUrl?: string };

export function joinBoard(boardId: string, displayName?: string, avatarUrl?: string) {
  getSocket().emit('board:join', { boardId, displayName, avatarUrl });
}

export function leaveBoard(boardId: string) {
  getSocket().emit('board:leave', { boardId });
}

export function sendBoardCursor(boardId: string, x: number, y: number, displayName?: string, avatarUrl?: string) {
  getSocket().emit('board:cursor', { boardId, x, y, displayName, avatarUrl });
}

export function onBoardPresence(handler: (payload: { boardId: string; members: BoardMember[] }) => void) {
  const s = getSocket();
  const fn = (p: any) => handler({ boardId: String(p?.boardId || ''), members: Array.isArray(p?.members) ? p.members : [] });
  s.on('board:presence', fn);
  return () => { try { s.off('board:presence', fn); } catch {} };
}

export function onBoardCursor(handler: (payload: { fromUserId: string; boardId: string; x: number; y: number; displayName?: string; avatarUrl?: string }) => void) {
  const s = getSocket();
  const fn = (p: any) => handler({ fromUserId: String(p?.fromUserId || ''), boardId: String(p?.boardId || ''), x: Number(p?.x || 0), y: Number(p?.y || 0), displayName: p?.displayName, avatarUrl: p?.avatarUrl });
  s.on('board:cursor', fn);
  return () => { try { s.off('board:cursor', fn); } catch {} };
}

export function sendBoardStateUpdate(boardId: string) {
  getSocket().emit('board:state-update', { boardId });
}

export function onBoardStateUpdate(handler: (payload: { boardId: string; fromUserId: string }) => void) {
  const s = getSocket();
  const fn = (p: any) => handler({ boardId: String(p?.boardId || ''), fromUserId: String(p?.fromUserId || '') });
  s.on('board:state-update', fn);
  return () => { try { s.off('board:state-update', fn); } catch {} };
}

// EasyEstimate — obecność przy otwartej zakładce Kosztorys (estimate)
export type EasyEstimateKosztorysMember = { userId: string; displayName?: string; avatarUrl?: string };

export function joinEasyEstimateKosztorys(
  estimateId: string,
  displayName?: string,
  avatarUrl?: string,
) {
  getSocket().emit('easyEstimate:join', { estimateId, displayName, avatarUrl });
}

export function leaveEasyEstimateKosztorys(estimateId: string) {
  getSocket().emit('easyEstimate:leave', { estimateId });
}

export function onEasyEstimateKosztorysPresence(
  handler: (payload: { estimateId: string; members: EasyEstimateKosztorysMember[] }) => void,
) {
  const s = getSocket();
  const fn = (p: any) =>
    handler({
      estimateId: String(p?.estimateId || ''),
      members: Array.isArray(p?.members) ? p.members : [],
    });
  s.on('easyEstimate:presence', fn);
  return () => {
    try {
      s.off('easyEstimate:presence', fn);
    } catch {}
  };
}

export type EasyEstimateRemoteSheetCell = {
  cellId: string | null;
  displayName?: string;
};

export function emitEasyEstimateSheetCell(
  estimateId: string,
  cellId: string | null,
  displayName?: string,
  avatarUrl?: string,
) {
  getSocket().emit('easyEstimate:sheet-cell', { cellId, displayName, avatarUrl });
}

export function onEasyEstimateSheetCell(
  handler: (payload: {
    estimateId: string;
    fromUserId: string;
    cellId: string | null;
    displayName?: string;
    avatarUrl?: string;
  }) => void,
) {
  const s = getSocket();
  const fn = (p: any) =>
    handler({
      estimateId: String(p?.estimateId || ''),
      fromUserId: String(p?.fromUserId || ''),
      cellId: p?.cellId == null || p?.cellId === '' ? null : String(p.cellId),
      displayName: typeof p?.displayName === 'string' ? p.displayName : undefined,
      avatarUrl: typeof p?.avatarUrl === 'string' ? p.avatarUrl : undefined,
    });
  s.on('easyEstimate:sheet-cell', fn);
  return () => {
    try {
      s.off('easyEstimate:sheet-cell', fn);
    } catch {}
  };
}

/** Snapshot przedmiaru (bez zapisu do DB) — synchronizacja między klientami przy otwartym kosztorysie. */
export type EasyEstimatePrzedmiarBroadcastPayload = {
  tableEntries: unknown;
  cellOverrides: Record<string, string>;
  calcScratchBundles: unknown;
};

export function emitEasyEstimatePrzedmiarBroadcast(
  estimateId: string,
  payload: EasyEstimatePrzedmiarBroadcastPayload,
) {
  getSocket().emit('easyEstimate:przedmiar-broadcast', {
    estimateId,
    tableEntries: payload.tableEntries,
    cellOverrides: payload.cellOverrides,
    calcScratchBundles: payload.calcScratchBundles,
  });
}

export function onEasyEstimatePrzedmiarBroadcast(
  handler: (payload: EasyEstimatePrzedmiarBroadcastPayload & { estimateId: string; fromUserId: string }) => void,
) {
  const s = getSocket();
  const fn = (p: any) =>
    handler({
      estimateId: String(p?.estimateId || ''),
      fromUserId: String(p?.fromUserId || ''),
      tableEntries: p?.tableEntries,
      cellOverrides:
        typeof p?.cellOverrides === 'object' && p.cellOverrides !== null && !Array.isArray(p.cellOverrides)
          ? (p.cellOverrides as Record<string, string>)
          : {},
      calcScratchBundles: p?.calcScratchBundles,
    });
  s.on('easyEstimate:przedmiar-broadcast', fn);
  return () => {
    try {
      s.off('easyEstimate:przedmiar-broadcast', fn);
    } catch {}
  };
}

// ===== STT – globalne sesje transkrypcji (presence) =====

export type SttActiveSession = {
  meetingGoogleId: string;
  meetingId: number | null;
  meetingTitle: string | null;
  startedBy: { userId: string; displayName: string | null; avatarUrl: string | null };
  startedAt: string;
  participantUserIds: string[];
  jobId: string | null;
};

export function startSttSession(meetingId: string) {
  const s = getSocket();
  s.emit('stt:session:start', { meetingId });
}

export function stopSttSession(meetingId: string) {
  const s = getSocket();
  s.emit('stt:session:stop', { meetingId });
}

export function requestActiveSttSessions() {
  const s = getSocket();
  s.emit('stt:active-sessions:get');
}

function mapSession(p: any): SttActiveSession {
  return {
    meetingGoogleId: String(p?.meetingGoogleId || ''),
    meetingId: p?.meetingId != null ? Number(p.meetingId) : null,
    meetingTitle: p?.meetingTitle || null,
    startedBy: {
      userId: String(p?.startedBy?.userId || ''),
      displayName: p?.startedBy?.displayName || null,
      avatarUrl: p?.startedBy?.avatarUrl || null,
    },
    startedAt: String(p?.startedAt || ''),
    participantUserIds: Array.isArray(p?.participantUserIds) ? p.participantUserIds.map(String) : [],
    jobId: p?.jobId || null,
  };
}

export function onSttSessionStarted(handler: (s: SttActiveSession) => void) {
  const s = getSocket();
  const fn = (p: any) => handler(mapSession(p));
  s.on('stt:session:started', fn);
  return () => { try { s.off('stt:session:started', fn); } catch {} };
}

export function onSttSessionStopped(handler: (meetingGoogleId: string) => void) {
  const s = getSocket();
  const fn = (p: any) => handler(String(p?.meetingGoogleId || ''));
  s.on('stt:session:stopped', fn);
  return () => { try { s.off('stt:session:stopped', fn); } catch {} };
}

export function onSttActiveSessions(handler: (sessions: SttActiveSession[]) => void) {
  const s = getSocket();
  const fn = (p: any) => {
    const arr = Array.isArray(p?.sessions) ? p.sessions.map(mapSession) : [];
    handler(arr);
  };
  s.on('stt:active-sessions', fn);
  return () => { try { s.off('stt:active-sessions', fn); } catch {} };
}

export function onSttNoteGenerated(handler: (p: { meetingGoogleId: string; noteId: string; noteTitle: string }) => void) {
  const s = getSocket();
  const fn = (p: any) => handler({
    meetingGoogleId: String(p?.meetingGoogleId || ''),
    noteId: String(p?.noteId || ''),
    noteTitle: String(p?.noteTitle || ''),
  });
  s.on('stt:note-generated', fn);
  return () => { try { s.off('stt:note-generated', fn); } catch {} };
}

export function onAiJobsChanged(handler: () => void) {
  const s = getSocket();
  const fn = () => handler();
  s.on('ai-jobs:changed', fn);
  return () => { try { s.off('ai-jobs:changed', fn); } catch {} };
}

export function onSttHistory(handler: (meetingId: string, items: SttHistoryEntry[]) => void) {
  const s = getSocket();
  const fn = (p: any) => {
    const meetingId = String(p?.meetingId || '');
    const list = Array.isArray(p?.items) ? p.items.map((x: any) => ({
      meetingId: String(x?.meetingId || meetingId),
      userId: String(x?.userId || ''),
      displayName: x?.displayName || null,
      avatarUrl: x?.avatarUrl || null,
      text: String(x?.text || ''),
      atIso: x?.atIso || null
    })) : [];
    handler(meetingId, list);
  };
  s.on('stt:history', fn);
  return () => { try { s.off('stt:history', fn); } catch {} };
}
